import React, { Component } from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';
import { logSessionInfo } from '../../services/logger';
import { COPY_STRINGS } from '../../constants/translatedCopy';
import { markCursorBehaviorFTUEComplete, CURSOR_BEHAVIOR } from '../../services/CursorOrchestrator';
import NextButton from './NextButton';
import './TutorFTUE.scss';

/**
 * TutorFTUE - Generic First Time User Experience component for tutor mode features
 * 
 * Supports multiple FTUE types:
 * - 'cursor-free': Walkthrough for cursor-free tutor mode
 *   - Variants: 'standard' (shows cursor animation), 'post-assessment' (no cursor)
 * - 'tap-to-read': Walkthrough for tap-to-read feature
 * 
 * Each type has its own flow with custom VOs, animations, and UI elements.
 */
class TutorFTUE extends Component {
  constructor(props) {
    super(props);
    
    this.state = {
      ftueStep: null,  // 'intro', 'how_it_works', 'with_cursor', 'without_cursor', 'read_underlined', 'tap_demo', 'next_button'
      showContent: false,
      cursorEnabled: false,
      showNextButton: false,
      highlightNextButton: false,
      currentHighlightedWord: -1, // For animating cursor/taps through words
      showFingerPointer: false, // For tap-to-read FTUE
    };
    
    this._isMounted = false;
    this.wordAnimationTimer = null;
  }

  componentDidMount() {
    this._isMounted = true;
    // Start the FTUE flow immediately
    this.runFTUE();
  }

  componentWillUnmount() {
    this._isMounted = false;
    this.clearWordAnimationTimer();
  }

  wait = (ms) => {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  clearWordAnimationTimer = () => {
    if (this.wordAnimationTimer) {
      clearInterval(this.wordAnimationTimer);
      this.wordAnimationTimer = null;
    }
  }

  animateWordsAcrossPhrase = (numWords = 4, intervalMs = 600) => {
    // Animate cursor through first N words of the first phrase
    let wordIndex = 0;
    
    this.setState({ currentHighlightedWord: 0 });
    
    this.wordAnimationTimer = setInterval(() => {
      wordIndex++;
      if (wordIndex >= numWords) {
        this.clearWordAnimationTimer();
        this.setState({ currentHighlightedWord: -1 }); // Clear highlight after animation
      } else {
        this.setState({ currentHighlightedWord: wordIndex });
      }
    }, intervalMs);
  }

  runFTUE = async () => {
    const { type } = this.props;
    
    try {
      // Delegate to type-specific flow
      if (type === 'cursor-free') {
        await this.runCursorFreeFTUE();
      } else if (type === 'tap-to-read') {
        await this.runTapToReadFTUE();
      } else {
        console.error(`Unknown FTUE type: ${type}`);
        this.completeWithError(new Error(`Unknown FTUE type: ${type}`));
      }
    } catch (error) {
      this.completeWithError(error);
    }
  }

  completeWithError = (error) => {
    console.error(`Error in ${this.props.type} FTUE:`, error);
    logSessionInfo(`${this.props.type}_ftue_error`, { error: error?.message });
    
    // Clean up timer
    this.clearWordAnimationTimer();
    
    // Notify parent even on error so story can continue
    if (this._isMounted && this.props.onComplete) {
      this.props.onComplete();
    }
  }

  runCursorFreeFTUE = async () => {
      const variant = this.props.variant || 'standard'; // 'standard' or 'post-assessment'
      const isPostAssessment = variant === 'post-assessment';
      
      logSessionInfo('cursor_free_ftue_started', { variant });
      
      // Step 1: Introduction (blank screen with coach)
      if (!this._isMounted) return;
      this.setState({
        ftueStep: 'intro',
        showContent: false
      });
      
      // Use different intro based on variant
      const introText = isPostAssessment 
        ? COPY_STRINGS.cursor_free_ftue.post_assess_intro 
        : COPY_STRINGS.cursor_free_ftue.intro;
      
      await this.props.speech.speakWithPromise({
        textToSpeak: introText,
        useGenerative: true
      });
      
      if (!this._isMounted) return;
      await this.wait(1000);

      // Step 2: Show first phrase (WITH cursor animation for standard, WITHOUT for post-assessment)
      if (!this._isMounted) return;
      this.setState({
        ftueStep: 'without_cursor',
        showContent: true,
        cursorEnabled: !isPostAssessment, // Enable cursor only for standard variant
        showNextButton: false,
        currentHighlightedWord: -1
      });
      
      // Only animate cursor for standard variant
      if (!isPostAssessment) {
        // Start word animation (4 words, 600ms each = 2.4s total)
        this.animateWordsAcrossPhrase(4, 600);
      }
      
      // Use different VO based on variant
      const noCursorText = isPostAssessment
        ? COPY_STRINGS.cursor_free_ftue.post_assess_no_cursor
        : COPY_STRINGS.cursor_free_ftue.no_cursor_explanation;
      
      // Play VO while cursor animates (or just play VO for post-assessment)
      await this.props.speech.speakWithPromise({
        textToSpeak: noCursorText,
        useGenerative: true
      });
      
      // Ensure animation is done and cursor is hidden
      this.clearWordAnimationTimer();
      
      if (!this._isMounted) return;
      this.setState({
        cursorEnabled: false,
        currentHighlightedWord: -1
      });
      
      await this.wait(1500);

      // Step 3: Show second phrase WITHOUT Next button, explain automatic progression
      if (!this._isMounted) return;
      this.setState({
        ftueStep: 'with_cursor', // Show second phrase
        cursorEnabled: false,
        showNextButton: false, // Hide button during automatic_progression
        highlightNextButton: false
      });
      
      await this.props.speech.speakWithPromise({
        textToSpeak: COPY_STRINGS.cursor_free_ftue.automatic_progression,
        useGenerative: true
      });
      
      if (!this._isMounted) return;
      await this.wait(1500);

      // Step 4: Show Next button (phrase already visible), explain it
      if (!this._isMounted) return;
      this.setState({
        showNextButton: true, // Now show the button
        highlightNextButton: true
      });
      
      await this.props.speech.speakWithPromise({
        textToSpeak: COPY_STRINGS.cursor_free_ftue.next_button_explanation,
        useGenerative: true
      });
      
      if (!this._isMounted) return;
      await this.wait(1500);

      // Step 5: Final message (unhighlight button)
      if (!this._isMounted) return;
      this.setState({
        highlightNextButton: false
      });
      
      await this.props.speech.speakWithPromise({
        textToSpeak: COPY_STRINGS.cursor_free_ftue.lets_go,
        useGenerative: true
      });
      
      if (!this._isMounted) return;
      await this.wait(2000);

      // Mark as completed (explicitly the cursor-free FTUE this component represents)
      await markCursorBehaviorFTUEComplete(CURSOR_BEHAVIOR.CURSOR_FREE);
      
      logSessionInfo('cursor_free_ftue_completed', { variant });
      
      // Notify parent that FTUE is complete
      if (this._isMounted && this.props.onComplete) {
        this.props.onComplete();
      }
  }

  runTapToReadFTUE = async () => {
    logSessionInfo('tap_to_read_ftue_started');
    
    // Step 1: Introduction - device/internet error (blank screen with coach)
    if (!this._isMounted) return;
    this.setState({
      ftueStep: 'intro',
      showContent: false
    });
    
    await this.props.speech.speakWithPromise({
      textToSpeak: COPY_STRINGS.tap_to_read_ftue.intro,
      useGenerative: true
    });
    
    if (!this._isMounted) return;
    await this.wait(1000);

    // Step 2: "Here's how it will work" - show content without highlighting
    if (!this._isMounted) return;
    this.setState({
      ftueStep: 'how_it_works',
      showContent: true,
      currentHighlightedWord: -1, // No highlighting yet
      showNextButton: false
    });
    
    await this.props.speech.speakWithPromise({
      textToSpeak: COPY_STRINGS.tap_to_read_ftue.how_it_works,
      useGenerative: true
    });
    
    if (!this._isMounted) return;
    await this.wait(1000);

    // Step 3: Show first word underlined, explain to read it
    if (!this._isMounted) return;
    this.setState({
      ftueStep: 'read_underlined',
      currentHighlightedWord: 0, // Highlight first word
      showFingerPointer: true, // Show pointer on first word
      showNextButton: false
    });
    
    await this.props.speech.speakWithPromise({
      textToSpeak: COPY_STRINGS.tap_to_read_ftue.read_underlined,
      useGenerative: true
    });
    
    if (!this._isMounted) return;
    await this.wait(1500);

    // Step 4: Show tap behavior - first just ghost on second word
    if (!this._isMounted) return;
    
    // Show pointer on second word (ghost), but don't highlight it yet
    this.setState({
      ftueStep: 'tap_demo',
      currentHighlightedWord: -1, // No word highlighted during VO
      showFingerPointer: true // Show pointer on ghost word (second word)
    });
    
    // Play VO explaining tap behavior (while second word shows ghost underline + pointer)
    await this.props.speech.speakWithPromise({
      textToSpeak: COPY_STRINGS.tap_to_read_ftue.tap_next,
      useGenerative: true
    });
    
    // After VO, highlight second word (pointer already showing)
    if (!this._isMounted) return;
    this.setState({
      currentHighlightedWord: 1 // Now highlight second word
    });
    
    // Keep this state for 1 second
    await this.wait(1000);
    
    if (!this._isMounted) return;
    // Remove pointer before transitioning to last word
    this.setState({
      showFingerPointer: false
    });
    
    await this.wait(300);

    // Step 5: Show Next button with last word highlighted
    if (!this._isMounted) return;
    
    // Calculate last word index
    const demoPhraseLine1 = COPY_STRINGS.tap_to_read_ftue.demo_phrase_1;
    const phrase1Words = demoPhraseLine1.split(' ').filter(word => word !== "" && word !== " ");
    const lastWordIndex = phrase1Words.length - 1;
    
    this.setState({
      ftueStep: 'next_button',
      currentHighlightedWord: lastWordIndex, // Highlight last word
      showNextButton: true,
      highlightNextButton: true
    });
    
    await this.props.speech.speakWithPromise({
      textToSpeak: COPY_STRINGS.tap_to_read_ftue.next_button_explanation,
      useGenerative: true
    });
    
    if (!this._isMounted) return;
    await this.wait(1500);

    // Step 6: Final "Let's go" message
    if (!this._isMounted) return;
    this.setState({
      highlightNextButton: false,
      currentHighlightedWord: -1
    });
    
    await this.props.speech.speakWithPromise({
      textToSpeak: COPY_STRINGS.tap_to_read_ftue.lets_go,
      useGenerative: true
    });
    
    await this.wait(1000);

    // Mark as completed
    await markCursorBehaviorFTUEComplete(CURSOR_BEHAVIOR.TAP_TO_READ);
    
    logSessionInfo('tap_to_read_ftue_completed');
    
    // Notify parent that FTUE is complete
    if (this._isMounted && this.props.onComplete) {
      this.props.onComplete();
    }
  }

  render() {
    const { type } = this.props;
    
    // Delegate rendering to type-specific method
    if (type === 'cursor-free') {
      return this.renderCursorFree();
    } else if (type === 'tap-to-read') {
      return this.renderTapToRead();
    }
    
    return null;
  }

  renderCursorFree() {
    const { showContent, ftueStep, currentHighlightedWord, cursorEnabled } = this.state;
    const variant = this.props.variant || 'standard';
    const isPostAssessment = variant === 'post-assessment';
    
    // Only render content when showContent is true
    if (!showContent) {
      return null;
    }
    
    // Get demo phrases from translated copy
    const demoPhraseLine1 = COPY_STRINGS.cursor_free_ftue.demo_phrase_1;
    const demoPhraseLine2 = COPY_STRINGS.cursor_free_ftue.demo_phrase_2;
    
    // Split phrases into words
    const phrase1Words = demoPhraseLine1.split(' ').filter(word => word !== "" && word !== " ");
    const phrase2Words = demoPhraseLine2.split(' ').filter(word => word !== "" && word !== " ");
    
    // Determine current phrase index - matches Reader.js getPhraseClass() logic
    // During 'without_cursor': phrase 0 is current, phrase 1 is future
    // During 'with_cursor': phrase 0 is done, phrase 1 is current
    const currentPhraseIndex = ftueStep === 'with_cursor' ? 1 : 0;
    
    // Always render both phrases (like Reader.js does), CSS classes control visibility
    return (
      <>
        <div className={cx('phrase', 'shortStory', {
          'done': currentPhraseIndex > 0,
          'current': currentPhraseIndex === 0,
          'future': currentPhraseIndex < 0
        })}>
          {phrase1Words.map((word, index) => (
            <span 
              key={index} 
              className={cx('word', {
                // Only show highlighted/underlined cursor for standard variant
                'highlighted': !isPostAssessment && cursorEnabled && currentHighlightedWord === index,
                'success': currentPhraseIndex > 0
              })}
            >
              {word}{' '}
            </span>
          ))}
        </div>
        
        {/* Phrase 1 - Second demo phrase with Next button */}
        <div className={cx('phrase', 'shortStory', {
          'done': currentPhraseIndex > 1,
          'current': currentPhraseIndex === 1,
          'future': currentPhraseIndex < 1
        })}>
          <div className="phraseContent cursorFree">
            <div className="phraseText">
              {phrase2Words.map((word, index) => (
                <span key={index} className="word">
                  {word}{' '}
                </span>
              ))}
            </div>
            {/* Fixed-width container for Next button to prevent horizontal jumping */}
            <div className="ftueNextButtonContainer">
              {this.state.showNextButton && (
                <div className={cx('pointerTarget', {
                  'showPointer': this.state.highlightNextButton,
                  'left': this.state.highlightNextButton
                })}>
                  <NextButton
                    className="showNextButton"
                    onClick={() => {}} // No-op for FTUE
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      </>
    );
  }

  renderTapToRead() {
    const { showContent, ftueStep, currentHighlightedWord, showFingerPointer } = this.state;
    
    // Only render content when showContent is true
    if (!showContent) {
      return null;
    }
    
    // Get demo phrases from translated copy
    const demoPhraseLine1 = COPY_STRINGS.tap_to_read_ftue.demo_phrase_1;
    
    // Split phrase into words
    const phrase1Words = demoPhraseLine1.split(' ').filter(word => word !== "" && word !== " ");
    
    // Use demo phrase words for tap-to-read animation
    const allWords = [...phrase1Words];
    
    // Calculate ghost index
    // During tap_demo with no highlight (-1), show ghost on second word (index 1)
    // Otherwise, ghost is the next word after current
    const ghostIndex = (ftueStep === 'tap_demo' && currentHighlightedWord === -1) 
      ? 1 // Second word gets ghost during tap_demo VO
      : currentHighlightedWord + 1;
    
    // Determine if button should be visible (not just shown in DOM)
    const isButtonVisible = this.state.showNextButton && ftueStep === 'next_button';
    
    // Render directly without wrapper to match Reader.scss structure (.reader .phrase)
    return (
      <div className={cx('phrase', 'shortStory', 'current', 'tapToReadContainer')}>
        <div className="tapToReadWordsWrapper">
          <div>
          {allWords.map((word, index) => {
            const isHighlighted = currentHighlightedWord === index;
            const isGhostNext = index === ghostIndex;
            const isPastWord = currentHighlightedWord > index;
            
            // Show pointer on:
            // 1. Currently highlighted word during read_underlined or tap_demo
            // 2. Ghost word (second word) during tap_demo when no word is highlighted (VO phase)
            const shouldShowPointer = (
              (isHighlighted && showFingerPointer && (ftueStep === 'read_underlined' || ftueStep === 'tap_demo')) ||
              (isGhostNext && showFingerPointer && ftueStep === 'tap_demo' && currentHighlightedWord === -1)
            );
            
            const wordSpan = (
              <span 
                key={index} 
                className={cx('word', {
                  // Apply tap mode styling from Reader.scss
                  'highlighted': isHighlighted,
                  'tapMode': true, // Always apply tapMode class for user-select:none and tap-specific styles
                  // Show ghost next during read_underlined AND tap_demo steps for consistency
                  'ghostNext': isGhostNext && (ftueStep === 'read_underlined' || ftueStep === 'tap_demo'),
                  'success': isPastWord
                })}
              >
                {word}{' '}
              </span>
            );
            
            // Wrap with pointer target div when showing pointer
            if (shouldShowPointer) {
              return (
                <div 
                  key={index}
                  className={cx('pointerTargetWrapper', 'pointerTarget', 'showPointer', 'bottom')}
                >
                  {wordSpan}
                </div>
              );
            }
            
            return wordSpan;
          })}
          </div>
        </div>
        
        {/* Always render Next button to prevent page jump, but control visibility */}
        <div className={cx('pageNavigation', 'tapToReadNavigation', {
          'hidden': !isButtonVisible,
          'visible': isButtonVisible
        })}>
          <div className={cx('pointerTarget', {
            'showPointer': this.state.highlightNextButton && isButtonVisible,
            'left': this.state.highlightNextButton && isButtonVisible
          })}>
            <NextButton
              onClick={() => {}} // No-op for FTUE
            />
          </div>
        </div>
      </div>
    );
  }
}

TutorFTUE.propTypes = {
  type: PropTypes.oneOf(['cursor-free', 'tap-to-read']).isRequired,  // FTUE type
  speech: PropTypes.object.isRequired,  // Speech service with speakWithPromise
  onComplete: PropTypes.func.isRequired,  // Callback when FTUE is done
  variant: PropTypes.oneOf(['standard', 'post-assessment']),  // FTUE variant (for cursor-free type)
};

TutorFTUE.defaultProps = {
  variant: 'standard',
};

export default TutorFTUE;
